import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AppProvider } from './context/AppContext';
import { Auth0ContextProvider } from './context/Auth0Context';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ldmTheme from './theme';
// Import axios configuration to set up global defaults
import './config/axios';
import configService from './services/configService';

// Local development - HTTPS bypass disabled for localhost
console.log('🔧 Running in local development mode - no HTTPS bypass needed');

/* COMMENTED OUT - HTTPS bypass code causing issues in local development
if (window.location.protocol === 'http:') {
  console.log('🔧 Bypassing HTTPS requirement for testing...');

  // Store original values
  const originalLocation = window.location;
  const originalIsSecureContext = window.isSecureContext;

  // Override security context
  Object.defineProperty(window, 'isSecureContext', {
    value: true,
    writable: false,
    configurable: true
  });

  // Override location properties that Auth0 checks
  const mockLocation = {
    ...originalLocation,
    protocol: 'https:',
    href: originalLocation.href.replace('http:', 'https:'),
    origin: originalLocation.origin.replace('http:', 'https:'),
    toString: () => originalLocation.href.replace('http:', 'https:')
  };

  // Temporarily override location
  Object.defineProperty(window, 'location', {
    value: mockLocation,
    writable: true,
    configurable: true
  });

  // Restore after Auth0 loads (longer timeout)
  setTimeout(() => {
    try {
      Object.defineProperty(window, 'location', {
        value: originalLocation,
        writable: true,
        configurable: true
      });
      Object.defineProperty(window, 'isSecureContext', {
        value: originalIsSecureContext,
        writable: false,
        configurable: true
      });
      console.log('🔧 Restored original location properties');
    } catch (e) {
      console.warn('Could not restore location properties:', e);
    }
  }, 3000);
}
*/

// Loading component for configuration fetch
const ConfigLoader = ({ children }) => {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    configService.getConfig()
      .then(setConfig)
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div>Loading configuration...</div>
        <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#666' }}>
          Fetching secure configuration from server
        </div>
      </div>
    );
  }

  if (error) {
    // Auto-retry after 2 seconds instead of showing error page
    console.log('⚠️ Config load failed, retrying in 2 seconds...');
    setTimeout(() => {
      window.location.reload();
    }, 2000);

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{ fontSize: '1.2em', marginBottom: '20px' }}>
          Connecting to server...
        </div>
        <div style={{ fontSize: '0.9em', color: '#666' }}>
          Please wait
        </div>
      </div>
    );
  }

  // Auth0 configuration from backend - WITH REFRESH TOKENS ENABLED
  const auth0Config = {
    domain: config.auth0.domain,
    clientId: config.auth0.clientId,
    authorizationParams: {
      redirect_uri: config.auth0.redirectUri || window.location.origin,
      audience: config.auth0.audience,
      scope: config.auth0.scope || 'openid profile email read:assigned-projects create:wps update:timesheet offline_access'
    },
    useRefreshTokens: true, // Enable refresh tokens for better user experience
    cacheLocation: 'localstorage', // Use localStorage to persist tokens across page refreshes
    skipRedirectCallback: false, // Ensure proper redirect handling

    // Add error handling to prevent getting stuck on Auth0 error page
    onError: (error) => {
      console.error('🚨 Auth0 Error:', error);

      // Clear Auth0 cache on error
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            localStorage.removeItem(key);
          }
        });

        // Also clear session storage
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            sessionStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.warn('Could not clear Auth0 cache:', e);
      }

      // Just clean the URL if there's an error parameter, don't redirect
      // This allows the Auth0Login component to handle the situation
      if (window.location.search.includes('error=')) {
        console.log('🧹 Cleaning Auth0 error from URL');
        window.history.replaceState({}, document.title, '/login');
      }
    }
  };

  return children(auth0Config);
};

// Handle Auth0 redirect callback with error handling
const onRedirectCallback = (appState, user) => {
  console.log('🔄 Auth0 redirect callback:', { appState, user });

  // Use the returnTo route from appState, or default to the current path
  const returnTo = appState?.returnTo || window.location.pathname;
  window.history.replaceState({}, document.title, returnTo);
};

// Clear stale Auth0 data on app start to prevent token refresh issues
const clearStaleAuth0Data = () => {
  try {
    // Check for Auth0 error in URL (like the one you're experiencing)
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      console.log('🧹 Auth0 error detected in URL, clearing session:', { error, errorDescription });
      localStorage.clear();
      sessionStorage.clear();

      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  } catch (e) {
    console.log('Error clearing stale Auth0 data:', e);
  }
};

// Clear stale data on app initialization
clearStaleAuth0Data();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ConfigLoader>
      {(auth0Config) => (
        <Auth0Provider
          {...auth0Config}
          onRedirectCallback={onRedirectCallback}
        >
          <Auth0ContextProvider>
            <ThemeProvider theme={ldmTheme}>
              <CssBaseline />
              <AppProvider>
                <App />
              </AppProvider>
            </ThemeProvider>
          </Auth0ContextProvider>
        </Auth0Provider>
      )}
    </ConfigLoader>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
